import { parseFullName } from 'parse-full-name';

const BUSINESS_KEYWORD_PATTERN = /\b(LLC|L\.L\.C\.?|INC|INCORPORATED|CORP|CORPORATION|CO|CO\.|COMPANY|LTD|LIMITED|LP|L\.P\.|LLP|L\.L\.P\.|PLLC|P\.L\.L\.C\.?|PC|P\.C\.|TRUST|BANK|HOLDINGS?|REALTY|PROPERTIES|ASSOCIATES?|ENTERPRISES?|MANAGEMENT|PARTNERS?|GROUP|DBA)\b/i;
const ENTITY_SUFFIX_PATTERN = /\b(l\.?l\.?c\.?|inc\.?|corp\.?|co\.?|ltd\.?|lp\.?|llp\.?|pllc\.?|p\.?c\.?)\b/gi;
const ENTITY_SUFFIX_TOKEN_PATTERN = /^(l\.?l\.?c\.?|inc\.?|corp\.?|co\.?|ltd\.?|lp\.?|llp\.?|pllc\.?|p\.?c\.?)$/i;

function normalizeWhitespace(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function normalizeEntitySuffixes(name) {
  return name.replace(ENTITY_SUFFIX_PATTERN, match => match.replace(/\./g, '').toUpperCase());
}

function capitalizeAfterDash(value) {
  return String(value).replace(/-([a-z])/g, (_, c) => `-${c.toUpperCase()}`);
}

function capitalizeStandaloneInitials(value) {
  return String(value).replace(/\b([a-zA-Z])\b/g, match => match.toUpperCase());
}

function finalizeDisplayName(value) {
  return capitalizeStandaloneInitials(capitalizeAfterDash(value));
}

function titleCaseWord(value) {
  return finalizeDisplayName(value.charAt(0).toUpperCase() + value.slice(1).toLowerCase());
}

function normalizeBusinessToken(token) {
  const parts = token.split('-');
  return parts.map(part => {
    if (!part) return part;

    const leading = part.match(/^[^A-Za-z0-9]*/)?.[0] || '';
    const trailing = part.match(/[^A-Za-z0-9]*$/)?.[0] || '';
    const core = part.slice(leading.length, part.length - trailing.length);

    if (!core) return part;

    if (ENTITY_SUFFIX_TOKEN_PATTERN.test(core)) {
      return `${leading}${core.replace(/\./g, '').toUpperCase()}${trailing}`;
    }

    const coreNoDots = core.replace(/\./g, '');
    const isShortAcronym = /^[A-Z0-9&]+$/.test(coreNoDots) && coreNoDots.length >= 2 && coreNoDots.length <= 3;
    if (isShortAcronym) {
      return `${leading}${coreNoDots}${trailing}`;
    }

    return `${leading}${titleCaseWord(core)}${trailing}`;
  }).join('-');
}

function formatBusinessName(name) {
  return normalizeEntitySuffixes(
    name
    .split(/\s+/)
    .map(normalizeBusinessToken)
    .join(' ')
  );
}

export function isLikelyBusinessName(rawName) {
  const name = normalizeWhitespace(rawName);
  if (!name) return false;
  return BUSINESS_KEYWORD_PATTERN.test(name);
}

export function formatDisplayName(rawName) {
  const name = normalizeWhitespace(rawName);
  if (!name) return '';

  if (isLikelyBusinessName(name)) {
    return finalizeDisplayName(formatBusinessName(name));
  }

  const { first, middle, last, suffix } = parseFullName(name, 'all', 1);
  const parsed = [first, middle, last, suffix].filter(Boolean).join(' ');
  return finalizeDisplayName(parsed || name);
}
